import i18n from '@/i18n'
import ApiService from '@/services/ApiService'

export default class Form {

    constructor(component, formName, data) {
        this.$busy = false;
        this.$errors = {};
        this.$mutators = {}
        component.$nextTick(() => {
            this.$v = component.$v ? component.$v[formName] : null
        })

        Object.assign(this, data);
    }

    mutate(key, callback) {
        this.$mutators[key] = callback
    }

    /**
     * Start processing the form
     */
    startProcessing() {
        this.$busy = true;
        this.$errors = {};
    }

    /**
     * Finish processing the form
     */
    finishProcessing() {
        this.$busy = false;
    }

    /**
     * Reset the errors and other state for the form
     */
    reset() {
        this.$busy = false
        this.$errors = {}

        if(this.$v) {
            this.$v.$reset()
        }
    }

    /**
     * Validate the whole form or the specific field
     */
    validate(field = null) {
        if(!this.$v) {
            this.$errors = {}
            return true
        }

        if(!field) {
            let errors = {}
            this.$v.$touch()

            // Get all the fields to be validated
            const validatedFields = this.$v.$flattenParams()
                .map(param => param.path.join('.').replace('.$each',''))
                .filter((field, index, array) => array.indexOf(field) === index)

            validatedFields.forEach(fieldName => {
                errors[fieldName] = this.getFieldErrors(fieldName)
            })

            this.$errors = errors
            return this.isValid()
        }

        this.$errors = { ...this.$errors, [field]: this.getFieldErrors(field) }
        return this.isValid(field)
    }

    /**
     * Validate and return errors for the specific field
     */
    getFieldErrors(field) {
        if(!this.$v) {
            return this.$errors[field]
        }

        const [fieldName, index, key] = field.split('.')

        let errors = []

        const validation = !index
            ? this.$v[fieldName]
            : this.$v[fieldName].$each[index][key]

        if(!validation) {
            return errors
        }

        const rules = validation.$params

        validation.$touch()

        Object.values(rules).forEach(rule => {
            if(rule && !validation[rule.type]) {
                let error = Form.errorMessage(rule)
                if(!error) {
                    error = i18n.t('validation.general')
                }
                errors.push(error)
            }
        })

        return errors
    }

    /**
     * Get the first errof for the specified field
     */
    getError(field) {
        if(this.$errors[field] && this.$errors[field].length) {
            return this.$errors[field][0]
        }

        return null
    }

    /**
     * Check if the whole form or the specific field is valid
     */
    isValid(field = null) {
        if(field) {
            return !this.$errors[field] || !this.$errors[field].length
        }

        let valid = true
        Object.keys(this.$errors).forEach(field => {
            if(this.$errors[field] && this.$errors[field].length) {
                valid = false
            }
        })

        return valid
    }

    /**
     * Set the errors on the form
     */
    setErrors(errors) {
        const isObject = (value) => Object.prototype.toString.call(value) === '[object Object]'
        if(!isObject(errors)) return

        Object.keys(errors).forEach(key => {
            const errs = errors[key]
            errs.forEach((err, index) => {
                const msgTranslation = Form.errorMessage({ type: err })
                if(msgTranslation) {
                    errs[index] = msgTranslation
                }
            })
        })

        this.$errors = errors || {};
    }

    /**
     * Generate form data to send
     */
    toJson() {
        let jsonData = JSON.parse(JSON.stringify(this, (key, value) => {
            if(!['$errors', '$mutators', '$busy', '$v'].includes(key)) {
                return value === undefined ? null : value;
            }
        }));

        Object.keys(jsonData).forEach(key => {
            const mutate = this.$mutators[key]
            if(mutate) {
                jsonData[key] = mutate(jsonData[key])
            }
        })

        return jsonData;
    }

    /**
     * Helper method for making POST HTTP requests.
     */
    post(uri, config = null) {
        return this.send('post', uri, config);
    }

    /**
     * Helper method for making PUT HTTP requests.
     */
    put(uri, config = null) {
        return this.send('put', uri, config);
    }

    /**
     * Helper method for making PATCH HTTP requests.
     */
    patch(uri, config = null) {
        return this.send('patch', uri, config);
    }

    /**
     * Helper method for making DELETE HTTP requests.
     */
    delete(uri, config = null) {
        return this.send('delete', uri, config);
    }

    /**
     * Send the form to the back-end server.
     */
    send(method, uri, config = null) {
        return new Promise((resolve, reject) => {
            this.startProcessing();

            ApiService.http[method](uri, this.toJson(), config)
                .then(response => {
                    this.finishProcessing();
                    resolve(response);
                })
                .catch(errors => {
                    this.finishProcessing();

                    if(errors.response) {
                        this.setErrors(errors.response.data.errors);
                    }

                    reject(errors);
                });
        })
    }

    /**
     * Get validation messages
     */
    static errorMessage(rule) {
        switch(rule.type) {
            case 'required':
                return i18n.t('validation.required')
            case 'requiredIf':
                return i18n.t('validation.required')
            case 'requiredUnless':
                return i18n.t('validation.required')
            case 'minLength':
                return i18n.t('validation.min_length', { value: rule.min })
            case 'maxLength':
                return i18n.t('validation.max_length', { value: rule.max })
            case 'minValue':
                return i18n.t('validation.min_value', { value: rule.min })
            case 'maxValue':
                return i18n.t('validation.max_value', { value: rule.max })
            case 'between':
                return i18n.t('validation.between', { min: rule.min, max: rule.max })
            case 'alpha':
                return i18n.t('validation.alpha')
            case 'alphaNum':
                return i18n.t('validation.alpha_num')
            case 'numeric':
                return i18n.t('validation.numeric')
            case 'integer':
                return i18n.t('validation.integer')
            case 'decimal':
                return i18n.t('validation.decimal')
            case 'email':
                return i18n.t('validation.email')
            case 'ipAddress':
                return i18n.t('validation.ip_address')
            case 'macAddress':
                return i18n.t('validation.mac_address')
            case 'unique':
                return i18n.t('validation.unique')
            case 'sameAs':
                return i18n.t('validation.same_as')
            case 'url':
                return i18n.t('validation.url')
            default:
                return ''
        }
    }

    /**
     * Encode the file coming from file input with base64
     * and set encoded value to the form field
     */
    async encodeFile(field, file) {
        const base64File = await Form.fileToBase64(file);
        this[field] = base64File || null;
    }

    /**
     * Encode the file coming from file input with base64
     */
    static fileToBase64(file) {
        return new Promise((resolve, reject) => {
            if(!file) resolve(null);

            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                resolve(reader.result)
            };
        });
    }
}
